import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { attendanceAPI } from '../../../services';
import { useApi, useAsyncAction } from '../../../hooks';
import {
  Button,
  LoadingSpinner,
  Badge,
  ErrorMessage,
  SuccessMessage,
  Modal,
} from '../../UI';
import { ClipboardList, Plus, Clock, Calendar, CheckCircle2, XCircle, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';

const RegularizationPage = () => {
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [workTimings, setWorkTimings] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    date: '',
    type: 'forgot_punch',
    requestedPunchIn: '',
    requestedPunchOut: '',
    reason: ''
  });

  // Fetch work timings on mount
  useEffect(() => {
    const fetchWorkTimings = async () => {
      try {
        const response = await attendanceAPI.getMyWorkTimings();
        if (response.success) {
          setWorkTimings(response.workTimings);
        }
      } catch (err) {
        console.error('Failed to fetch work timings:', err);
      }
    };
    fetchWorkTimings();
  }, []);

  const { data: regularizationsData, loading: regularizationsLoading, refetch } = useApi(
    () => attendanceAPI.getMyRegularizations({ month: selectedMonth, year: selectedYear }),
    [selectedMonth, selectedYear]
  );

  const { loading: applyLoading, execute: executeApply } = useAsyncAction();

  const regularizations = regularizationsData?.regularizations || [];

  const regularizationTypes = [
    { value: 'forgot_punch', label: 'Forgot to Punch' },
    { value: 'missing_punch_in', label: 'Missing Punch In' },
    { value: 'missing_punch_out', label: 'Missing Punch Out' },
    { value: 'wrong_location', label: 'Wrong Location' },
    { value: 'system_error', label: 'System Error' },
    { value: 'other', label: 'Other' }
  ];

  // Helper function to validate if time is within shift
  const isTimeWithinShift = (timeStr) => {
    if (!workTimings || !timeStr) return true;
    
    const [hours, minutes] = timeStr.split(':').map(Number);
    const timeMinutes = hours * 60 + minutes;
    
    const [startH, startM] = workTimings.startTime.split(':').map(Number);
    const [endH, endM] = workTimings.endTime.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    
    // Buffer of 60 minutes before start and after end
    const bufferMinutes = 60;
    const minAllowed = startMinutes - bufferMinutes;
    const maxAllowed = endMinutes + bufferMinutes;
    
    // Handle night shift (end time < start time)
    if (endMinutes < startMinutes) {
      // Night shift spans midnight
      return timeMinutes >= minAllowed || timeMinutes <= maxAllowed;
    }
    
    return timeMinutes >= minAllowed && timeMinutes <= maxAllowed;
  };

  // Get shift info display text
  const getShiftInfo = () => {
    if (!workTimings) return null;
    const formatTime12 = (time) => {
      const [h, m] = time.split(':').map(Number);
      const period = h >= 12 ? 'PM' : 'AM';
      const hour12 = h % 12 || 12;
      return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
    };
    const shiftName = workTimings.shiftName || 'Work';
    return `${shiftName} Timings: ${formatTime12(workTimings.startTime)} - ${formatTime12(workTimings.endTime)}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.date || !formData.reason) {
      setError('Date and reason are required');
      return;
    }

    // Validate that requested punch times are provided for relevant types
    if (['forgot_punch', 'missing_punch_in'].includes(formData.type) && !formData.requestedPunchIn) {
      setError('Please provide the requested punch in time');
      return;
    }

    if (['forgot_punch', 'missing_punch_out'].includes(formData.type) && !formData.requestedPunchOut) {
      setError('Please provide the requested punch out time');
      return;
    }

    // Validate times are within shift boundaries
    if (workTimings) {
      if (formData.requestedPunchIn && !isTimeWithinShift(formData.requestedPunchIn)) {
        setError(`Punch In time must be within your ${workTimings.shiftName || 'work'} timings (${workTimings.startTime} - ${workTimings.endTime}) ± 1 hour`);
        return;
      }
      if (formData.requestedPunchOut && !isTimeWithinShift(formData.requestedPunchOut)) {
        setError(`Punch Out time must be within your ${workTimings.shiftName || 'work'} timings (${workTimings.startTime} - ${workTimings.endTime}) ± 1 hour`);
        return;
      }
    }

    try {
      const submitData = {
        date: formData.date,
        type: formData.type,
        reason: formData.reason
      };

      // Construct full datetime from date and time
      if (formData.requestedPunchIn) {
        submitData.requestedPunchIn = new Date(`${formData.date}T${formData.requestedPunchIn}`);
      }
      if (formData.requestedPunchOut) {
        submitData.requestedPunchOut = new Date(`${formData.date}T${formData.requestedPunchOut}`);
      }

      const result = await executeApply(attendanceAPI.applyRegularization, submitData);

      if (result.success) {
        setSuccess('Regularization request submitted successfully!');
        setShowApplyModal(false);
        setFormData({
          date: '',
          type: 'forgot_punch',
          requestedPunchIn: '',
          requestedPunchOut: '',
          reason: ''
        });
        refetch();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to submit regularization request');
      }
    } catch (err) {
      setError(err.message || 'Failed to submit request');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'warning', label: 'Pending' },
      approved: { variant: 'success', label: 'Approved' },
      rejected: { variant: 'danger', label: 'Rejected' },
      lop: { variant: 'danger', label: 'LOP' }
    };
    const config = statusConfig[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getTypeLabel = (type) => {
    const typeLabels = {
      forgot_punch: 'Forgot to Punch',
      missing_punch_in: 'Missing Punch In',
      missing_punch_out: 'Missing Punch Out',
      wrong_location: 'Wrong Location',
      system_error: 'System Error',
      other: 'Other'
    };
    return typeLabels[type] || type;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    if (!date) return '--:--';
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const goToPrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Get counts for stats
  const pendingCount = regularizations.filter(r => r.status === 'pending').length;
  const approvedCount = regularizations.filter(r => r.status === 'approved').length;
  const lopCount = regularizations.filter(r => r.status === 'lop' || r.isLOP).length;

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Attendance Regularization</h2>
          <p className="text-gray-500">Apply for attendance corrections and view request status</p>
        </div>
        <Button
          onClick={() => setShowApplyModal(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4" />
          Apply Regularization
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Approved</p>
              <p className="text-2xl font-bold text-green-600">{approvedCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">LOP</p>
              <p className="text-2xl font-bold text-red-600">{lopCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Month Selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />
            My Regularization Requests
          </h3>
          <div className="flex items-center gap-4">
            <button onClick={goToPrevMonth} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-medium text-gray-900">
              {months[selectedMonth - 1]} {selectedYear}
            </span>
            <button onClick={goToNextMonth} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {regularizationsLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : regularizations.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No regularization requests found for this month</p>
            <Button
              onClick={() => setShowApplyModal(true)}
              variant="outline"
              className="mt-4"
            >
              Apply for Regularization
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comments</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {regularizations.map((reg) => (
                  <tr key={reg._id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {formatDate(reg.date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {getTypeLabel(reg.type)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex flex-col">
                        {reg.requestedPunchIn && (
                          <span className="text-green-600">In: {formatTime(reg.requestedPunchIn)}</span>
                        )}
                        {reg.requestedPunchOut && (
                          <span className="text-red-600">Out: {formatTime(reg.requestedPunchOut)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {reg.reason}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(reg.status)}
                      {reg.isLOP && reg.lopDeduction > 0 && (
                        <span className="ml-2 text-xs text-red-600">
                          -₹{reg.lopDeduction?.toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {reg.reviewComments || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Apply Modal */}
      <Modal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        title="Apply for Attendance Regularization"
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Shift Info Display */}
          {workTimings && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <Clock className="w-4 h-4 inline-block mr-1" />
                {getShiftInfo()}
              </p>
            </div>
          )}

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Regularization Type <span className="text-red-500">*</span>
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {regularizationTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Requested Punch In Time */}
          {['forgot_punch', 'missing_punch_in', 'wrong_location', 'system_error', 'other'].includes(formData.type) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Requested Punch In Time
                {['forgot_punch', 'missing_punch_in'].includes(formData.type) && <span className="text-red-500">*</span>}
              </label>
              <input
                type="time"
                name="requestedPunchIn"
                value={formData.requestedPunchIn}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          {/* Requested Punch Out Time */}
          {['forgot_punch', 'missing_punch_out', 'wrong_location', 'system_error', 'other'].includes(formData.type) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Requested Punch Out Time
                {['forgot_punch', 'missing_punch_out'].includes(formData.type) && <span className="text-red-500">*</span>}
              </label>
              <input
                type="time"
                name="requestedPunchOut"
                value={formData.requestedPunchOut}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              rows={3}
              placeholder="Please provide a detailed reason for this regularization request..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <AlertTriangle className="w-4 h-4 inline-block mr-1" />
              <strong>Note:</strong> If this request is rejected, the day will be marked as Loss of Pay (LOP).
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowApplyModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={applyLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Submit Request
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default RegularizationPage;
